<template>
  <div>
    <div
      v-for="ongoingImport in internalOngoingImports"
      :key="ongoingImport.id"
      class="
        sf-layer-border
        sf-layer-rounded
        sf-bg-primary-subtle
        sf-c-primary-dark
        sf-pad-3
        sf-below-2
      "
    >
      <div class="sf-level">
        <div class="sf-level-item">
          <div
            v-if="ongoingImport.status !== 'completed'"
            class="sf-spinner sf-spinner-primary"
          ></div>
          <div
            v-if="ongoingImport.status === 'completed'"
            class="fa fa-check sf-c-primary-dark"
          ></div>
        </div>
        <div class="sf-level-item">
          In progress: {{ ongoingImport.companies_updated_count }}/{{
            ongoingImport.total_companies_count
          }}
          client files updated
        </div>
      </div>
    </div>
    <div
      class="
        sf-layer-border
        sf-layer-rounded
        sf-bg-warning-subtle-2
        sf-c-warning-light-2
        sf-pad-3
        sf-below-2
      "
    >
      <div class="sf-level">
        <div class="sf-level-item">
          <div class="fa fa-solid fa-triangle-exclamation"></div>
        </div>
        <div class="sf-level-item sf-font-m">
          <div class="sf-pad-1">
            <div class="sf-below-1">
              <p>
                <span>The list of folders below will be the default for</span
                ><span class="sf-bold sf-c-warning"> newly added </span
                ><span>client files.</span>
              </p>
              <p>
                <span>For</span
                ><span class="sf-bold sf-c-warning"> existing </span
                ><span>client files the behaviour is as follows:</span>
              </p>
              <ul class="sf-ul">
                <li class="sf-ul-li">
                  Adding a folder will create a folder in each client file.
                </li>
                <li class="sf-ul-li">
                  <span>Removing a folder will</span
                  ><span class="sf-bold sf-c-warning"> not </span
                  ><span
                    >cause the folder to be removed in each client file.</span
                  >
                </li>
              </ul>
            </div>
            <div>
              Any unintentional changes will have to be reverted manually in
              each client file.
            </div>
          </div>
        </div>
      </div>
    </div>
    <SfFolders :key="childKey" :folders="currentStructure"></SfFolders>
    <div class="sf-below-1"></div>
    <div class="sf-btn sf-btn-primary sf-btn-small" @click="addFolder()">
      Add Folder
    </div>
    <div class="sf-below-3 sf-align-r">
      <div
        :class="{ 'sf-btn-disabled': saving || hasOngoingImports() }"
        class="sf-below-1 sf-btn sf-btn-success"
        @click="saveStructure()"
      >
        <div v-if="saving || hasOngoingImports()" class="sf-level">
          <div class="sf-level-item sf-spinner"></div>
          <div v-if="saving" class="sf-level-item">Saving</div>
          <div v-else class="sf-level-item">Pushing</div>
        </div>
        <span v-else>Save and Push</span>
      </div>
      <div class="sf-dim-el">
        The default will be set for newly added AND existing client files
      </div>
    </div>
  </div>
</template>

<script>
import SFetch from '@app/utils/sfetch'
import SfFolders from './folders'

// WARNING If you come here for inspiration for your own components, this is
// not the best example:
//
// There is a bunch of shortcuts that have been taken in this component and
// it's child Folders in folders.js:
// - modify the props directly
// - abuse $forceUpdate
// - make API call directly in method
// It could be done in a more proper way, but it would involve the use of a
// Vuex store, which would increase complexity, and since this is for an admin
// special action that is not supposed to evolved, the shortcuts have been
// deemed acceptable.

const FirmDefaultPermanentCategories = {
  components: {
    SfFolders,
  },
  props: {
    firmId: {
      type: Number,
      required: true,
    },
    currentStructure: {
      type: Array,
      required: false,
      default() {
        return []
      },
    },
    ongoingImports: {
      type: Array,
      required: false,
      default() {
        return []
      },
    },
    lastMessageBusId: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      saving: false,
      childKey: 0,
      internalOngoingImports: [...this.ongoingImports],
    }
  },
  mounted() {
    if (this.lastMessageBusId && this.internalOngoingImports.length > 0) {
      this.subscribe(parseInt(this.lastMessageBusId))
    }
  },
  methods: {
    addFolder() {
      this.currentStructure.push({
        title: '',
        private: true,
        editable: true,
      })
      this.reRender()
    },
    cleanData(folders) {
      return folders
        .filter((folder) => folder.title !== '')
        .map((folder) => {
          delete folder.editable
          delete folder.closed
          folder.sub_folders =
            folder.sub_folders && this.cleanData(folder.sub_folders)
          return folder
        })
    },
    saveStructure() {
      window.sfConfirm({
        question: 'Are you sure?',
        message:
          'Any unintentional changes will have to be reverted manually in each client file.',
        confirmLabel: 'Save and push',
        cancelLabel: I18n.t('js.global_confirmation_modal.default.cancel'),
        onConfirm: this.saveConfirmed,
        options: {
          message_class: 'sf-bg-snow sf-c-text',
          confirm_class: 'sf-btn-warning',
        },
      })
    },
    saveConfirmed() {
      this.saving = true
      const cleanedFolderStructure = this.cleanData(this.currentStructure)
      const path =
        Silverfin.Routes.admin_firm_special_actions_firm_default_permanent_categories_path(
          {
            firm_id: this.firmId,
          }
        )

      SFetch.json(path, {
        method: 'POST',
        body: { defaultPermanentCategories: cleanedFolderStructure },
      })
        .then(() => {
          window.Turbolinks.visit(path)
        })
        .catch((error) => {
          if (error.status !== 0) {
            alert('Something went wrong: ' + error.body)
            this.saving = false
          }
        })
    },
    reRender() {
      this.childKey += 1
    },
    subscribe(lastMessageBusId) {
      Silverfin.Polling.subscribe(
        '/folder_structure_imports/updated',
        this.importUpdated,
        lastMessageBusId
      )
    },
    unsubscribe() {
      Silverfin.Polling.unsubscribe(
        '/folder_structure_imports/updated',
        this.importUpdated
      )
    },
    importUpdated(messageBusPayload) {
      this.internalOngoingImports.forEach((ongoingImport) => {
        if (ongoingImport.id === messageBusPayload.folder_structure_import_id) {
          ongoingImport.companies_updated_count =
            messageBusPayload.companies_updated_count
          ongoingImport.status = messageBusPayload.status
        }
      })

      if (
        this.internalOngoingImports.every(
          (ongoingImport) => ongoingImport.status === 'completed'
        )
      ) {
        this.unsubscribe()
      }
    },
    hasOngoingImports() {
      return this.internalOngoingImports.some(
        (ongoingImport) => ongoingImport.status !== 'completed'
      )
    },
  },
}

export default FirmDefaultPermanentCategories
</script>
